import { Link } from 'react-router-dom';
import { Hero } from '@/components/hero/Hero';
import { ProjectRail } from '@/components/gallery/ProjectRail';
import { FadeIn } from '@/components/motion/FadeIn';
import { SEOHead } from '@/components/layout/SEOHead';
import { getAllProjects, getContact, getAllJournalPosts } from '@/lib/content';
import { buildInteriorDesignerSchema, buildWebSiteSchema, buildSchemaGraph } from '@/lib/seo';
import Manifesto, { frontmatter as manifestoFrontmatter } from '../../content/manifesto.mdx';

const SERVICES = [
  {
    eyebrow: 'I',
    title: 'Residential',
    body: 'Private homes, second residences, and historic cottages. We work with a small number of families each year, and the work begins with extended listening.',
  },
  {
    eyebrow: 'II',
    title: 'Hospitality',
    body: 'Restaurants, boutique lodging, and small-format hospitality. Spaces composed for thousands of strangers, where every detail survives the evening.',
  },
  {
    eyebrow: 'III',
    title: 'Commercial',
    body: 'Showrooms, ateliers, and bespoke commercial interiors. Spaces that work for the trades, the team, and the people who come through the door.',
  },
];

function formatJournalDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return iso; }
}

export function HomePage() {
  const projects = getAllProjects();
  const journal = getAllJournalPosts().slice(0, 2);
  const contact = getContact();
  const schema = buildSchemaGraph(
    buildInteriorDesignerSchema(contact),
    buildWebSiteSchema(),
  );
  const eyebrow = (manifestoFrontmatter['eyebrow'] as string) ?? 'Manifesto';

  return (
    <>
      <SEOHead
        title="Brian Ellison Design — Multidisciplinary Interior Design"
        description="Luxury interior design from Melbourne, Florida. Residential, commercial, and hospitality. Best of Houzz 2025."
        canonical="https://brianellisondesign.com/"
        ogImage="/posters/og-default.jpg"
        schema={schema}
      />
      <Hero />

      <section className="manifesto section">
        <div className="container manifesto__inner">
          <FadeIn>
            <p className="eyebrow">{eyebrow}</p>
          </FadeIn>
          <FadeIn delay={80}>
            <div className="manifesto__body drop-cap">
              <Manifesto />
            </div>
          </FadeIn>
        </div>
      </section>

      <p className="ornament container" aria-hidden="true">§</p>

      <section className="services-teaser section">
        <div className="container">
          <FadeIn>
            <p className="eyebrow">Practice</p>
            <h2 className="services-teaser__heading">
              Three kinds of room, one way of working.
            </h2>
          </FadeIn>
          <div className="services-teaser__grid">
            {SERVICES.map((s, i) => (
              <FadeIn key={s.title} delay={i * 80}>
                <article className="services-teaser__card">
                  <p className="eyebrow services-teaser__roman">{s.eyebrow}</p>
                  <h3 className="services-teaser__title">{s.title}.</h3>
                  <p className="services-teaser__body">{s.body}</p>
                </article>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={300}>
            <div className="services-teaser__cta">
              <Link to="/about" className="cta-ghost">How we work</Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <ProjectRail
        projects={projects}
        heading="Recent work"
        cta={{ label: 'View all projects', to: '/projects' }}
      />

      {journal.length > 0 ? (
        <section className="home-journal section">
          <div className="container">
            <header className="home-journal__header">
              <FadeIn>
                <p className="eyebrow">From the journal</p>
                <h2 className="home-journal__heading">Notes from the studio.</h2>
              </FadeIn>
            </header>
            <div className="home-journal__grid">
              {journal.map((post, i) => (
                <FadeIn key={post.slug} delay={i * 80}>
                  <Link to={`/journal/${post.slug}`} className="home-journal__entry">
                    <p className="eyebrow">{formatJournalDate(post.date)}{post.category ? ` · ${post.category}` : ''}</p>
                    <h3 className="home-journal__title">{post.title}</h3>
                    <p className="home-journal__excerpt">{post.excerpt}</p>
                    <span className="text-link home-journal__more">Read entry →</span>
                  </Link>
                </FadeIn>
              ))}
            </div>
            <FadeIn delay={300}>
              <div className="home-journal__cta">
                <Link to="/journal" className="cta-ghost">All entries</Link>
              </div>
            </FadeIn>
          </div>
        </section>
      ) : null}

      <section className="home-inquiry section">
        <div className="container home-inquiry__inner">
          <FadeIn>
            <p className="eyebrow">Begin a project</p>
            <h2 className="home-inquiry__heading">
              Considering a residence, restaurant, or boutique commercial space in
              <em> Brevard County</em>?
            </h2>
            <p className="subtitle">
              Brian works with a small number of clients each year to ensure each
              project receives the attention it deserves.
            </p>
            <div className="home-inquiry__cta">
              <Link to="/contact" className="cta-primary">Get in touch</Link>
              <Link to="/about" className="cta-ghost">About the studio</Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
